import { Botao, Campo, Entrada, Selecao } from '@baiocchi/ui'
import { useMemo } from 'react'

import { diasAtras, formatarData, hojeFiltro } from '../utilitarios/formatar'

export interface AtalhoPeriodo {
  valor: string
  rotulo: string
  /** `null` em "Vitalício": remove o recorte de período. */
  dias: number | null
}

export const ATALHOS_PERIODO: ReadonlyArray<AtalhoPeriodo> = [
  { valor: 'hoje', rotulo: 'Hoje', dias: 0 },
  { valor: '7', rotulo: 'Últimos 7 dias', dias: 7 },
  { valor: '30', rotulo: 'Últimos 30 dias', dias: 30 },
  { valor: '90', rotulo: 'Últimos 90 dias', dias: 90 },
  { valor: 'vitalicio', rotulo: 'Vitalício', dias: null },
]

export interface PropriedadesFiltroPeriodo {
  de?: string
  ate?: string
  onAlterar: (periodo: { de?: string; ate?: string }) => void
  /** Rótulo do intervalo em uso — "Período: 11/07/2026 a 10/08/2026" da TELA-01. */
  mostrarResumo?: boolean
}

/**
 * Filtro de período com atalhos.
 *
 * As datas são enviadas em `YYYY-MM-DD` e a API as interpreta como **dia civil de
 * São Paulo** (RN-55) — por isso os atalhos são calculados por
 * `paraDataFiltro`, e não por `toISOString()`, que devolveria o dia UTC e
 * deslocaria o "hoje" do corretor depois das 21h.
 */
export function FiltroPeriodo({ de, ate, onAlterar, mostrarResumo = true }: PropriedadesFiltroPeriodo) {
  const atalhoAtivo = useMemo(() => {
    if (!de && !ate) return 'vitalicio'
    const hoje = hojeFiltro()
    if (ate !== hoje) return 'personalizado'
    for (const atalho of ATALHOS_PERIODO) {
      if (atalho.dias === null) continue
      if (de === (atalho.dias === 0 ? hoje : diasAtras(atalho.dias))) return atalho.valor
    }
    return 'personalizado'
  }, [de, ate])

  function aplicarAtalho(valor: string) {
    const atalho = ATALHOS_PERIODO.find((item) => item.valor === valor)
    if (!atalho) return
    if (atalho.dias === null) {
      onAlterar({ de: undefined, ate: undefined })
      return
    }
    onAlterar({
      de: atalho.dias === 0 ? hojeFiltro() : diasAtras(atalho.dias),
      ate: hojeFiltro(),
    })
  }

  return (
    <div className="flex flex-wrap items-end gap-3">
      <Campo rotulo="Período" className="w-full sm:w-52">
        {(atributos) => (
          <Selecao
            {...atributos}
            value={atalhoAtivo}
            onChange={(evento) => aplicarAtalho(evento.target.value)}
            opcoes={[
              ...ATALHOS_PERIODO.map((atalho) => ({ valor: atalho.valor, rotulo: atalho.rotulo })),
              { valor: 'personalizado', rotulo: 'Personalizado', desabilitada: true },
            ]}
          />
        )}
      </Campo>

      <Campo rotulo="De" className="w-full sm:w-40">
        {(atributos) => (
          <Entrada
            {...atributos}
            type="date"
            value={de ?? ''}
            max={ate ?? undefined}
            onChange={(evento) => onAlterar({ de: evento.target.value || undefined, ate })}
          />
        )}
      </Campo>

      <Campo rotulo="Até" className="w-full sm:w-40">
        {(atributos) => (
          <Entrada
            {...atributos}
            type="date"
            value={ate ?? ''}
            min={de ?? undefined}
            onChange={(evento) => onAlterar({ de, ate: evento.target.value || undefined })}
          />
        )}
      </Campo>

      <Botao variante="contorno" onClick={() => onAlterar({ de: undefined, ate: undefined })}>
        Limpar
      </Botao>

      {mostrarResumo ? (
        <p className="w-full text-xs text-conteudo-fraco sm:w-auto sm:self-center">
          {de || ate
            ? `Exibindo ${formatarData(de ? `${de}T12:00:00Z` : null, 'início')} a ${formatarData(
                ate ? `${ate}T12:00:00Z` : null,
                'hoje',
              )}`
            : 'Exibindo todo o histórico'}
        </p>
      ) : null}
    </div>
  )
}
